import axios from "axios";
import { addStoreIdToUrl, isDevelopment } from "../utils/storeIdManager";

// Função para determinar a URL da API baseada no ambiente
const getApiBaseUrl = (): string => {
  // Em produção (não localhost), sempre usar api.stackb2b.com.
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname !== 'localhost' && hostname !== '127.0.0.1') {
      return "https://api.stackb2b.com";
    }
  }
  // Dev: permite override via NEXT_PUBLIC_API_URL ou NEXT_PUBLIC_API_URL_DEV.
  // Fallback prático para a API de produção, para facilitar preview local.
  const envUrl = (process.env.NEXT_PUBLIC_API_URL || process.env.NEXT_PUBLIC_API_URL_DEV);
  if (envUrl) return envUrl.replace(/\/+$/, "");
  return "https://api.stackb2b.com";
};

// Resolve API base URL
const API_BASE = getApiBaseUrl();

// Debug log para verificar qual URL está sendo usada
if (typeof window !== 'undefined') {
  console.log(`🌐 API Base URL: ${API_BASE} (hostname: ${window.location.hostname})`);
}

// Configuração global para axios
axios.defaults.withCredentials = true;

const axiosInstance = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  // Configurar para processar cookies corretamente em todas as requisições
  headers: {
    "Content-Type": "application/json",
    "Accept": "application/json",
  }
});

// Function to get stored token
const getStoredToken = (): string | null => {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('authToken');
};

// Function to store token
export const setAuthToken = (token: string | null) => {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem('authToken', token);
  } else {
    localStorage.removeItem('authToken');
  }
};

// Interceptor para configurar cabeçalhos
axiosInstance.interceptors.request.use((config) => {
  // Adicionar o cabeçalho personalizado para identificação do host
  if (typeof window !== "undefined") {
    config.headers["X-Requested-From"] = window.location.origin;
    
    // Garantir que withCredentials está sempre definido para true
    config.withCredentials = true;
    
    // Adicionar o token ao cabeçalho Authorization se disponível
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Para desenvolvimento local, adicionar storeId automaticamente usando o gerenciador
    if (isDevelopment() && config.url) {
      config.url = addStoreIdToUrl(config.url);
    }
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

// Interceptor para lidar com a resposta e armazenar o token se disponível
axiosInstance.interceptors.response.use(
  (response) => {
    // Se a resposta contiver token, armazená-lo (não só no login, mas em qualquer resposta)
    if (response.data?.token) {
      console.log("Token recebido e atualizado");
      setAuthToken(response.data.token);
    }
    return response;
  },
  (error) => {
    // Se o erro for 401 (Unauthorized), podemos limpar o token
    if (error.response && error.response.status === 401) {
      console.log("Erro 401: Limpando token");
      setAuthToken(null);
      
      // Redirecionar para login se não estiver na página de login
      if (typeof window !== 'undefined' && !window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;